import { get } from 'lodash';
import getColour from '@lendi-ui/commons/colours';
import ModeEdit from '@lendi-ui/icon/ModeEdit';
import MoreVert from '@lendi-ui/icon/MoreVert';
import MenuDropdown from '@lendi-ui/menu-dropdown';

import { FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { ApplicantEntityIndex, FormValuesApplicants, StoreApplicant } from '@funnels/types';
import { run } from '@funnels/utils';

import { TempCard, TempCardContent, TempCardHeader, TempCardHeaderText } from '../../../Card/index.style';
import { PanelMenu, PanelMenuDropdownContentItem, PanelMenuIconButton } from '../_common/style';
import { CompanyApplicantSummaryLayout } from './companyLayouts';
import { ApplicantSummaryLayout } from './layouts';
import { TrustApplicantSummaryLayout } from './trustLayouts';

interface ApplicantsSummaryPanelProps extends ApplicantEntityIndex {
  currentValues: FormValuesApplicants;
  handleModalShow: (clientId: string) => void;
  handleRedirect: (clientId: string) => void;
  goToSection: (clientId: string, section: string) => void;
  applicationLockIsActive: boolean;
}

const LABELS = { EDIT: 'Edit applicant', DELETE: 'Delete' };

const ApplicantSummaryPanel = (props: ApplicantsSummaryPanelProps) => {
  const { tracking } = useAnalytics();
  const { currentValues, applicantIndex, handleModalShow, handleRedirect, goToSection, applicationLockIsActive } =
    props;

  const applicant: StoreApplicant | undefined = get(currentValues, `${FIELD_PATHS.APPLICANTS()}[${applicantIndex}]`);
  // Make one of them return '' instead of undefined so we dont get undefined === undefined
  const primaryApplicantId = get(currentValues, FIELD_PATHS.APPLICATION_PRIMARY_APPLICANT_ID(0));
  const isPrimary =
    primaryApplicantId !== undefined &&
    primaryApplicantId === get(currentValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));

  const currentApplicantType = get(
    currentValues,
    FIELD_PATHS.APPLICANT_ENTITY(applicantIndex),
    '' as FIELD_VALUES.TRUST | FIELD_VALUES.COMPANY | FIELD_VALUES.PERSON
  );

  if (!applicant) {
    return null;
  }

  function handleEditClick() {
    applicant && handleRedirect(applicant.clientId);
  }

  function handleDeleteClick() {
    if (!isPrimary) {
      applicant && handleModalShow(applicant.clientId);
    }
  }

  function RenderSummaryLayout() {
    switch (currentApplicantType) {
      case FIELD_VALUES.TRUST:
        return (
          <TrustApplicantSummaryLayout
            formValues={currentValues}
            applicantIndex={applicantIndex}
            goToSection={goToSection}
          />
        );

      case FIELD_VALUES.COMPANY:
        return (
          <CompanyApplicantSummaryLayout
            formValues={currentValues}
            applicantIndex={applicantIndex}
            goToSection={goToSection}
          />
        );

      case FIELD_VALUES.PERSON:
      default:
        return (
          <ApplicantSummaryLayout
            currentValues={currentValues}
            applicantIndex={applicantIndex}
            goToSection={goToSection}
          />
        );
    }
  }

  return (
    <>
      <TempCard>
        <TempCardHeader>
          <TempCardHeaderText>
            Applicant {isPrimary ? `${applicantIndex + 1} - Primary` : `${applicantIndex + 1}`}{' '}
            {!get(currentValues, FIELD_PATHS.APPLICANTS()).every(
              (applicant: StoreApplicant) => applicant.entity === FIELD_VALUES.PERSON
            ) && `(${currentApplicantType.toLowerCase()})`}
          </TempCardHeaderText>
          <PanelMenu>
            <PanelMenuIconButton
              onClick={run(handleEditClick, () => tracking?.button.icon({ text: LABELS.EDIT }))}
              color={getColour('interaction', 'subtle')}
              size="sm"
              icon={ModeEdit}
            />
            {!applicationLockIsActive && (
              <MenuDropdown>
                <MenuDropdown.Trigger data-component="button-applicant-menu">
                  <PanelMenuIconButton color={getColour('interaction', 'subtle')} size="sm" icon={MoreVert} />
                </MenuDropdown.Trigger>
                <MenuDropdown.Content>
                  <MenuDropdown.Content.Item
                    size="sm"
                    data-component="button-applicant-delete"
                    onClick={run<void>(handleDeleteClick, () => tracking?.button.emphasis({ text: LABELS.DELETE }))}
                  >
                    <PanelMenuDropdownContentItem isDisabled={isPrimary}>{LABELS.DELETE}</PanelMenuDropdownContentItem>
                  </MenuDropdown.Content.Item>
                </MenuDropdown.Content>
              </MenuDropdown>
            )}
          </PanelMenu>
        </TempCardHeader>
        <TempCardContent>
          <RenderSummaryLayout />
        </TempCardContent>
      </TempCard>
    </>
  );
};

export default ApplicantSummaryPanel;
